/**
 * DynamicGratuity Component
 *
 * Provides a toggle for users to enable/disable gratuity on appointments.
 * Only displays in markets where adjustable tip rates are enabled.
 *
 * - Toggle between 20% gratuity and no gratuity (0%)
 * - Booking flow automatically recalculates appointment price when changed
 * - Gated by market's adjustableTipRate setting
 *
 * Props:
 * @param {function} onTipRateChange - Callback to update tip rate (0 or 0.2)
 * @param {boolean} isTipRateAdjustableMarket - Whether market allows adjustable tips
 */
import React, { useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { gsColors } from '../../styles/Variables'
import { mq } from '../../helpers'
import ToggleSlider from '../ToggleSlider'
import { trackCheckoutTipRateSelected } from '../../helpers/tracking'

const GratuityContainer = styled.section`
  background-color: ${gsColors.white};
  margin: 50px 60px;
  border-top: 1px solid #c7c7c7;
  max-width: 600px;
  padding-top: 35px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mq('max', '768px')} {
    margin: 0 0 30px;
    padding: 30px 16px;
  }
`

const GratuityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 8px;
`

const GratuityContent = styled.div`
  display: flex;
  flex-direction: column;
`

const GratuityHeader = styled.h3`
  font-family: Euclid, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #000;
  line-height: normal;
  margin: 0;
`

const GratuityDescription = styled.p`
  font-family: Euclid, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #878787;
  line-height: normal;
  margin: 0;
`

const DynamicGratuity = ({ onTipRateChange, isTipRateAdjustableMarket, totalPrice }) => {
  const [gratuityEnabled, setGratuityEnabled] = useState(true)

  const toggleGratuity = () => {
    const newGratuityState = !gratuityEnabled
    const newRate = newGratuityState ? 0.2 : 0
    const previousRate = gratuityEnabled ? 0.2 : 0
    trackCheckoutTipRateSelected(newRate, previousRate, totalPrice)

    setGratuityEnabled(newGratuityState)
    onTipRateChange(newRate)
  }

  if (!isTipRateAdjustableMarket) {
    return null
  }

  return (
    <GratuityContainer>
      <GratuityRow>
        <GratuityContent>
          <GratuityHeader>
            Add Gratuity (20%)
          </GratuityHeader>
          <GratuityDescription>
            100% goes to beauty pros
          </GratuityDescription>
        </GratuityContent>
        <ToggleSlider
          checked={gratuityEnabled}
          onChange={toggleGratuity}
        />
      </GratuityRow>
    </GratuityContainer>
  )
}

DynamicGratuity.propTypes = {
  onTipRateChange: PropTypes.func.isRequired,
  isTipRateAdjustableMarket: PropTypes.bool,
  totalPrice: PropTypes.number,
}

export default DynamicGratuity
